// src/viqai/NetworkOpportunity.jsx
// ─── Opportunité réseau ─────────────────────────────────────────────────────
// Remplace l'ouverture d'un dossier diffusé dans la modale Dossier VIQ, qui
// restait bloquée sur « Chargement en cours… » : cette modale appelle
// /api/dossiers/:id, une route réservée au propriétaire. Un dossier diffusé
// n'appartient pas encore à celui qui le consulte.
//
// Le titre l'annonce d'ailleurs explicitement : « Opportunité réseau », et non
// « Dossier VIQ (85/85) » — l'utilisateur ne parcourt pas ses propres dossiers.

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost } from "./api";
import "./viqai-dark.css";

const eur = (c) => `${((c || 0) / 100).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
const eurRaw = (n) => (n == null ? null : `${Number(n).toLocaleString("fr-FR")} €`);

const txt = (v, fallback = "—") => (v === undefined || v === null || v === "" ? fallback : String(v));
const yn = (v) => v === true ? "Oui" : v === false ? "Non" : "—";
const num = (v, suffix = "") => (v === undefined || v === null || v === "" || !Number.isFinite(Number(v))) ? "—" : `${Number(v).toLocaleString("fr-FR")}${suffix}`;

function DetailBlock({ title, icon, available, rows = [], footer }) {
  return <section className={`network-proof-card${available ? " available" : " missing"}`}>
    <div className="network-proof-head"><span className="network-proof-icon">{icon}</span><div><strong>{title}</strong><small>{available ? "Donnée présente dans le Dossier VIQ" : "Non importé dans le dossier"}</small></div><span className={`network-proof-state${available ? " ok" : ""}`}>{available ? "✓" : "○"}</span></div>
    {available && <div className="network-proof-grid">{rows.filter(r => r && r[1] !== undefined && r[1] !== null && r[1] !== "").map(([label,value])=><div className="network-proof-row" key={label}><span>{label}</span><b>{value}</b></div>)}</div>}
    {available && footer && <div className="network-proof-footer">{footer}</div>}
  </section>;
}

// §70 — Un chargement sans fin n'est jamais acceptable. Au-delà de huit
// secondes on annonce l'échec et on propose de réessayer, plutôt que de
// laisser tourner une roue indéfiniment.
const LOAD_TIMEOUT_MS = 8000;

function OfferForm({ onSubmit, onCancel, busy, askingPrice }) {
  const [amount, setAmount] = useState(askingPrice ? String(askingPrice) : "");
  const [message, setMessage] = useState("");
  const [validity, setValidity] = useState(48);
  return (
    <div className="viq-card confirm">
      <div className="viq-card-title">Votre proposition</div>
      <label className="aff-field">
        <span className="aff-label">Montant</span>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="22 800" />
      </label>
      <label className="aff-field">
        <span className="aff-label">Message</span>
        <input value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Offre ferme après vérification administrative" />
      </label>
      <label className="aff-field">
        <span className="aff-label">Validité</span>
        <select value={validity} onChange={e => setValidity(Number(e.target.value))}>
          <option value={24}>24 h</option><option value={48}>48 h</option>
          <option value={72}>72 h</option><option value={168}>7 jours</option>
        </select>
      </label>
      <div className="viq-actions">
        <button className="viq-btn primary" disabled={busy || !Number(amount)}
          onClick={() => onSubmit({ amount_eur: Number(amount), message, validity_hours: validity })}>
          {busy ? "Envoi…" : "Envoyer l'offre"}
        </button>
        <button className="viq-btn" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

export default function NetworkOpportunity({ publicationId, onClose, onAskViq, onOpenDossier }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offering, setOffering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), LOAD_TIMEOUT_MS));
    try {
      const j = await Promise.race([apiGet(`/api/network/publications/${publicationId}`), timeout]);
      if (j?.success) setData(j);
      else setError(j?.error || "Cette publication n'est plus disponible.");
    } catch (e) {
      // La vraie erreur est journalisée : sans elle, un 403 et un timeout
      // réseau produisaient le même écran muet.
      console.warn("[network opportunity]", e.message);
      setError(e.message === "timeout"
        ? "Impossible de charger cette publication."
        : (e.message || "Cette publication n'est plus disponible."));
    }
    setLoading(false);
  }, [publicationId]);
  useEffect(() => { load(); }, [load]);

  const sendOffer = async (body) => {
    setBusy(true);
    const j = await apiPost(`/api/network/publications/${publicationId}/offers`, body).catch(e => ({ error: e.message }));
    setBusy(false);
    if (j?.success) {
      setOffering(false); load();
      // §18 — Les vitrines (carrousel, panneau, dashboard) se rafraîchissent
      // sans rechargement : le compteur d'offres doit monter immédiatement.
      window.dispatchEvent(new CustomEvent("viq-marketplace-changed", { detail: { publication_id: publicationId } }));
    }
    else alert(j?.error || "Offre non enregistrée.");
  };

  const decide = async (offerId, action, confirm = false) => {
    // v267 — l'ancien premier clic appelait volontairement l'API sans
    // confirmation. apiPost transforme le HTTP 428 en exception, donc
    // `needs_confirmation` n'atteignait jamais l'UI et le navigateur affichait
    // seulement « Confirmation requise ». La première étape est désormais
    // locale ; seule la confirmation explicite exécute l'attribution.
    if (action === "accept" && !confirm) {
      setConfirming(offerId);
      setNotice("");
      return;
    }
    setBusy(true);
    let j;
    try {
      j = await apiPost(`/api/network/offers/${offerId}/${action}`, confirm ? { confirm: true } : {});
    } catch (e) {
      const detail = e?.detail || null;
      if (detail?.needs_confirmation) {
        setConfirming(offerId);
        setBusy(false);
        return;
      }
      j = { error: detail?.error || e?.message || "Opération impossible." };
    }
    setBusy(false);
    if (j?.success) {
      setConfirming(null);
      window.dispatchEvent(new CustomEvent("viq-marketplace-changed", { detail: { publication_id: publicationId, awarded: action === "accept" && confirm } }));
      if (action === "accept" && confirm) {
        setNotice("Offre acceptée. Le Dossier VIQ a été attribué au professionnel et la publication est clôturée.");
      } else if (action === "reject") {
        setNotice("Offre refusée.");
      } else if (action === "withdraw") {
        setNotice("Offre retirée.");
      }
      await load();
    } else alert(j?.error || "Opération impossible.");
  };

  if (loading) {
    return <div className="aff-admin viq-themed"><div className="viq-empty">Chargement de l'opportunité…</div></div>;
  }
  if (error) {
    return (
      <div className="aff-admin viq-themed">
        <div className="aff-head"><h2>Opportunité réseau</h2>
          {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}</div>
        <div className="viq-card">
          <div className="viq-empty">{error}</div>
          <div className="viq-actions"><button className="viq-btn primary" onClick={load}>Réessayer</button></div>
        </div>
      </div>
    );
  }

  const p = data.publication;
  const isOwner = data.is_owner;
  const isAcquirer = data.is_acquirer;
  const details = p.dossier_details || {};
  const hv = details.histovec || { available:false };
  const cv = details.carvertical || { available:false };
  const cg = details.registration_card || { available:false };
  const checkpoints = details.checkpoints || {};
  const photos = Array.isArray(p.photos) ? p.photos.filter(Boolean) : [];

  return (
    <div className="aff-admin viq-themed network-opportunity-detail">
      <div className="aff-head">
        <h2>Opportunité réseau</h2>
        {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}
      </div>

      <div className="viq-card">
        <div className="viq-card-head">
          <div className="viq-card-title">{p.label}</div>
          <div className="viq-card-sub">
            {[p.year, p.km ? `${Number(p.km).toLocaleString("fr-FR")} km` : null].filter(Boolean).join(" · ")}
          </div>
        </div>

        {photos.length > 0 ? (
          <div className="network-photo-gallery" aria-label={`${photos.length} photo${photos.length>1?"s":""} du véhicule`}>
            {photos.slice(0, 8).map((src, i) => <button type="button" className="network-photo-button" key={`${src}-${i}`} onClick={() => setPhotoIndex(i)} aria-label={`Agrandir la photo ${i+1}`}><img src={src} alt={`Véhicule · photo ${i+1}`} loading="lazy" /></button>)}
          </div>
        ) : <div className="network-photo-empty">Aucune photo partagée avec cette publication.</div>}

        <div className="viq-rows" style={{ marginTop: ".7rem" }}>
          {p.version && <div className="viq-row"><span className="k">Version</span><span className="v">{p.version}</span></div>}
          {p.fuel && <div className="viq-row"><span className="k">Énergie</span><span className="v">{p.fuel}</span></div>}
          {p.gearbox && <div className="viq-row"><span className="k">Boîte</span><span className="v">{p.gearbox}</span></div>}
          {p.score != null && <div className="viq-row"><span className="k">Score VIQ.AI®</span><span className="v">{p.score}/100</span></div>}
          {p.location && <div className="viq-row"><span className="k">Secteur</span><span className="v">{p.location}</span></div>}
          <div className="viq-row"><span className="k">Publié par</span><span className="v">{isOwner ? "Votre publication" : p.owner_name}</span></div>
          {p.asking_price != null && (
            <div className="viq-row total"><span className="k">Prix souhaité</span><span className="v">{eurRaw(p.asking_price)}</span></div>
          )}
        </div>

        {!isOwner && (p.owner_email || p.owner_phone) && <div className="network-pro-contact">
          <div className="network-pro-contact-copy"><strong>Coordonnées du professionnel</strong><span>{p.owner_name || "Membre du réseau"}</span>{p.owner_email && <span>{p.owner_email}</span>}{p.owner_phone && <span>{p.owner_phone}</span>}</div>
          <div className="network-pro-contact-actions">{p.owner_email && <a href={`mailto:${p.owner_email}`}>✉ Email</a>}{p.owner_phone && <a href={`tel:${p.owner_phone}`}>☎ Appeler</a>}</div>
        </div>}

        {p.note && <div className="viq-card-sub network-publication-note">{p.note}</div>}

        <div className="network-traceability">
          <div className="network-section-head"><div><span>Traçabilité du Dossier VIQ</span><h3>Éléments de confiance partagés</h3></div><div className="network-checkpoints"><span className={checkpoints.photo_analysis?"done":""}>{checkpoints.photo_analysis?"✓":"○"} Analyse photo</span><span className={checkpoints.valuation?"done":""}>{checkpoints.valuation?"✓":"○"} Cote</span><span className={checkpoints.listing?"done":""}>{checkpoints.listing?"✓":"○"} Annonce</span></div></div>
          <div className="network-proof-list">
            <DetailBlock title="HistoVec" icon="🛡️" available={!!hv.available} rows={[
              ["Propriétaires", txt(hv.owners_count)], ["CT valide", yn(hv.ct_valid)], ["Dernier CT", txt(hv.last_ct)],
              ["Kilométrage connu", hv.last_mileage!=null?num(hv.last_mileage," km"):"—"], ["Sinistres", txt(hv.accidents_declared)],
              ["Gage", yn(hv.pledge)], ["Opposition", yn(hv.opposition)], ["Volé", yn(hv.stolen)]
            ]} footer={hv.administrative_status ? `Situation : ${hv.administrative_status}` : null}/>
            <DetailBlock title="CarVertical" icon="🔎" available={!!cv.available} rows={[
              ["Kilométrage vérifié", yn(cv.mileage_verified)], ["Dernier kilométrage", cv.last_mileage!=null?num(cv.last_mileage," km"):"—"],
              ["Propriétaires", txt(cv.owners_count)], ["Sinistres", txt(cv.accidents_declared)], ["Compteur trafiqué", yn(cv.mileage_rollback)],
              ["Véhicule volé", yn(cv.stolen)], ["Pays d’origine", txt(cv.origin_country)], ["Usage précédent", txt(cv.previous_usage)]
            ]} footer={cv.report_date ? `Rapport : ${cv.report_date}` : (cv.import_export ? `Immatriculations : ${cv.import_export}` : null)}/>
            <DetailBlock title="Carte grise" icon="📄" available={!!cg.available} rows={[
              ["1re mise en circulation", txt(cg.first_registration)], ["Marque", txt(cg.make)], ["Modèle", txt(cg.model)], ["Énergie", txt(cg.fuel)],
              ["Puissance fiscale", cg.fiscal_power!=null?`${cg.fiscal_power} CV`:"—"], ["CO₂", cg.co2!=null?`${cg.co2} g/km`:"—"],
              ["Carrosserie", txt(cg.body_type)], ["VIN", txt(cg.vin_masked)]
            ]} footer="Identité du titulaire et adresse masquées sur le réseau."/>
          </div>
        </div>

        {notice && <div className="network-action-notice" role="status">✓ {notice}</div>}

        {isAcquirer && p.status === "awarded" && (
          <div className="viq-card-sub" style={{ marginTop: ".8rem", fontWeight: 700 }}>
            Dossier acquis : il a été transféré dans votre espace VehicleIQ et n'est plus visible dans la vitrine réseau.
          </div>
        )}

        {/* Rappel permanent : ce qui n'est pas là ne l'est pas par oubli. */}
        <div className="viq-card-sub" style={{ marginTop: ".8rem" }}>
          Aucune coordonnée client n'est transmise avant attribution.
        </div>

        <div className="viq-actions">
          {isAcquirer && data.dossier_id && onOpenDossier && (
            <button className="viq-btn primary" onClick={() => onOpenDossier(data.dossier_id)}>Ouvrir le Dossier VIQ acquis</button>
          )}
          {!isOwner && !isAcquirer && p.status !== "awarded" && !offering && (
            <button className="viq-btn primary" onClick={() => setOffering(true)}>Faire une offre</button>
          )}
          <button className="viq-btn" onClick={() => onAskViq && onAskViq(`Analyse l'opportunité réseau ${p.label} proposée à ${eurRaw(p.asking_price) || "prix non communiqué"} : marché, risques, marge potentielle.`)}>
            Analyser avec VIQ.AI
          </button>
        </div>
      </div>

      {offering && (
        <OfferForm askingPrice={p.asking_price} busy={busy}
          onSubmit={sendOffer} onCancel={() => setOffering(false)} />
      )}

      {/* §9-10 — Le propriétaire voit les montants ; un autre membre ne voit que
          ses propres offres et le nombre total. */}
      <div className="aff-sub">
        {isOwner ? `Offres reçues (${data.offers.length})` : "Vos offres"}
      </div>
      {!data.offers.length && (
        <div className="viq-empty">
          {isOwner
            ? `${p.offers_count} professionnel${p.offers_count > 1 ? "s" : ""} intéressé${p.offers_count > 1 ? "s" : ""}.`
            : "Vous n'avez pas encore fait d'offre sur ce véhicule."}
        </div>
      )}
      {data.offers.map(o => (
        <div key={o.id} className="viq-card">
          <div className="viq-card-head">
            <span className="aff-name">{isOwner ? (o.bidder_name || "Membre du réseau") : "Votre offre"}</span>
            <span className={`viq-chip${o.status === "accepted" ? " accent" : o.status === "pending" ? " warn" : ""}`}>{o.status}</span>
          </div>
          {isOwner && (o.bidder_email || o.bidder_phone) && <div className="network-pro-contact">
            <div className="network-pro-contact-copy"><strong>Coordonnées de l'offrant</strong>{o.bidder_email && <span>{o.bidder_email}</span>}{o.bidder_phone && <span>{o.bidder_phone}</span>}</div>
            <div className="network-pro-contact-actions">{o.bidder_email && <a href={`mailto:${o.bidder_email}`}>✉ Email</a>}{o.bidder_phone && <a href={`tel:${o.bidder_phone}`}>☎ Appeler</a>}</div>
          </div>}
          <div className="viq-rows">
            <div className="viq-row"><span className="k">Montant</span><span className="v">{eur(o.amount_cents)}</span></div>
            {o.message && <div className="viq-row"><span className="k">Message</span><span className="v" style={{ fontWeight: 400, textAlign: "left" }}>{o.message}</span></div>}
            {o.expires_at && <div className="viq-row"><span className="k">Valable jusqu'au</span><span className="v">{new Date(o.expires_at).toLocaleString("fr-FR")}</span></div>}
          </div>

          {isOwner && o.status === "pending" && confirming !== o.id && (
            <div className="viq-actions">
              <button className="viq-btn primary" onClick={() => decide(o.id, "accept")}>Accepter l'offre</button>
              <button className="viq-btn" onClick={() => decide(o.id, "reject")}>Refuser</button>
            </div>
          )}

          {/* §11 — L'attribution est irréversible : elle est énoncée avant
              d'être exécutée, avec ses trois conséquences. */}
          {isOwner && confirming === o.id && (
            <div className="viq-card confirm" style={{ marginTop: ".7rem" }}>
              <div className="viq-card-title">Confirmer l'attribution</div>
              <div className="viq-answer" style={{ fontSize: ".88rem" }}>
                Vous êtes sur le point d'attribuer définitivement ce dossier à {o.bidder_name || "ce professionnel"}.
              </div>
              <ul className="viq-bullets">
                <li>la publication réseau sera clôturée ;</li>
                <li>les autres offres seront refusées ;</li>
                <li>le dossier lui sera transféré, en conservant son historique.</li>
              </ul>
              <div className="viq-actions">
                <button className="viq-btn primary" disabled={busy} onClick={() => decide(o.id, "accept", true)}>
                  {busy ? "Attribution…" : "Confirmer l'attribution"}
                </button>
                <button className="viq-btn" onClick={() => setConfirming(null)}>Annuler</button>
              </div>
            </div>
          )}

          {!isOwner && o.status === "pending" && (
            <div className="viq-actions">
              <button className="viq-btn" onClick={() => decide(o.id, "withdraw")}>Retirer mon offre</button>
            </div>
          )}
        </div>
      ))}

      {photoIndex != null && photos[photoIndex] && (
        <div className="network-photo-lightbox" role="dialog" aria-modal="true" aria-label={`Photo ${photoIndex+1} sur ${photos.length}`} onClick={() => setPhotoIndex(null)}>
          <button type="button" className="network-photo-close" onClick={() => setPhotoIndex(null)} aria-label="Fermer">✕</button>
          {photos.length > 1 && <button type="button" className="network-photo-nav prev" aria-label="Photo précédente" onClick={e => { e.stopPropagation(); setPhotoIndex((photoIndex - 1 + photos.length) % photos.length); }}>‹</button>}
          <img src={photos[photoIndex]} alt={`Véhicule · photo ${photoIndex+1}`} onClick={e => e.stopPropagation()} />
          {photos.length > 1 && <button type="button" className="network-photo-nav next" aria-label="Photo suivante" onClick={e => { e.stopPropagation(); setPhotoIndex((photoIndex + 1) % photos.length); }}>›</button>}
          <span className="network-photo-count">{photoIndex+1} / {photos.length}</span>
        </div>
      )}
    </div>
  );
}
